import styled from 'styled-components'
import { SubHeader } from '../GvSubHeader'
import { MenuSection } from './MenuSection'
import { MainImageArea } from '../MainImage/MainImageArea'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getGuidedViewPageDescriptionHidden,
  getMarketDenomination,
  getSessionInitDataState,
  pushPageViewTrackingEvent,
  setGuidedViewLoadTime,
  setGuidedViewPageDescriptionHidden,
  setMarketDenomination,
  setSeriesFrame,
  setShowFullPageViewer,
  setUnavailableImageFrames,
  setUserSelectionTimestamp,
} from '../../../store/sessionDataSlice'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PortraitFooter } from '../../../components/Footer/PortraitFooter'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { TdsButton, TdsIcon } from '@scania/tegel-react'
import { getTruckImageState } from '../../../store/truckImageSlice'
import { SvgChevronDown, SvgMaximize } from '../../../components/SvgImports'
import {
  CocOpenedFromType,
  GuidedViewPageType,
  ImageSeriesFrame,
} from '../../../store/types'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickFullImageEvent,
  ScaniaAdobeTrackingConfigChangedEvent,
  ScaniaAdobeTrackingPageName,
} from '../../../utils/adobeAnalytics'
import { getMarketLanguageState } from '../../../store/appSlice'
import { useResizeObserver } from '../../../utils/useResizeObserver'
import { Size2d } from '../../../types/Size2d'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import {
  getMenuState,
  setCurrentlyLoadingId,
  setMenuTreeGv,
} from '../../../store/menuSlice'
import {
  ConsequenceOfChangeDialogData,
  ConsequenceOfChangeHandler,
} from '../../../types/ConsequenceOfChangeTypes'
import { useClient } from '../../../utils/useClient'
import { SMART_DASH_FAMILY_ID } from '../../../api/constants'
import { SESSION_FAILURE } from '../../../api/errors'
import {
  ConfigChangeMenuEntry,
  ConfigChangeResult,
} from '../../../api/generated'
import {
  getTruckInfoState,
  setLatestConfigChangeResult,
} from '../../../store/truckInfoSlice'
import { closeAllModals } from '../../../store/modalSlice'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import { usePageUpdate } from '../../../utils/useBMPageUpdate'
import { MainImageControlPanelMobile } from '../MainImage/MainImageControlPanelMobile'
import { TestElementTypeId } from '../../../types/TestAttributeId'

interface GuidedViewRootProps {
  $pageIsReady: boolean
}

const GuidedViewRoot = styled.main<GuidedViewRootProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: white;
  visibility: ${(props) => (props.$pageIsReady ? '' : 'hidden')};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    overflow-y: auto;
    overflow-x: hidden;
    ${CustomScrollbarMixin}
  }
`

const MainArea = styled.div`
  display: flex;
  flex: 1;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex-direction: column;
    flex: none;
  }
`

const ImageSection = styled.section`
  width: 67%;
  display: flex;
  flex-direction: column;
  padding-left: 60px;
  padding-right: 40px;
  padding-bottom: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.XLarge}px) {
    padding-bottom: 0px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-left: 32px;
    padding-right: 20px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    height: calc(100vh - var(--header-height) - 90px);
    min-height: calc(100vh - var(--header-height) - 90px);
  }
`

const HeaderAndChevronWrapper = styled.div`
  display: flex;
  padding-top: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.XLarge}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-top: 0px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-top: 24px;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
  max-width: calc(100% - 24px);
`

const Header1 = styled.h1`
  margin-right: 16px;
  word-break: break-word;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: min(3rem, 8vw, 5vh);
    word-break: keep-all;
  }
`

const Header2 = styled.h1`
  font-weight: 100;
  word-break: break-word;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 32px;
    font-size: min(3rem, 8vw, 5vh);
    word-break: keep-all;
  }
`

interface ChevronProps {
  $hideText: boolean
}

const ChevronWrapper = styled.div<ChevronProps>`
  height: 24px;
  width: 24px;
  margin-left: 16px;

  transform: ${(props) =>
    props.$hideText ? 'rotate(0deg)' : 'rotate(-180deg)'};
  transition-duration: 150ms;
  transition-property: transform;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--tds-grey-300);
`

const GotItButtonWrapper = styled.div`
  width: 100%;
`

const PageDescription = styled.div`
  font-size: 14px;
  padding-top: 8px;
  padding-bottom: 4px;
`

const ImageWrapper = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    padding-left: 0px;
    flex-direction: column;
  }
`

const FullViewWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    position: absolute;
    right: 0px;
    top: 0px;
    background-color: white;
    display: unset;
  }

  svg {
    height: 20px;
  }
`

const ButtonsWrapperMobile = styled.div`
  bottom: 0px;
  background-color: white;
  border-top: 1px solid var(--tds-grey-500);
  width: 100vw;
  padding-top: 24px;
  padding-bottom: 16px;
  padding-right: 32px;
  padding-left: 32px;
  display: none;

  button {
    min-width: 200px;
    display: flex;
    justify-content: center;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }
`

export interface GuidedViewProps {
  handleNewClick: () => void
  handleRfqOrEysClick: () => void
  handleFatalError: () => void
  handleSessionFailure: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
  handleConsequenceOfChange: ConsequenceOfChangeHandler
  handleCloseModalConsequenseOfChange: () => void
  highlightedId: string | null
}

export function GuidedViewPage({
  handleNewClick,
  handleRfqOrEysClick,
  handleFatalError,
  handleSessionFailure,
  navigateToMenu,
  handleCloseModalConsequenseOfChange,
  handleConsequenceOfChange,
  highlightedId,
}: GuidedViewProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const t2 = useTextsV2()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLDivElement | null>(null)
  const marketDenomination = useAppSelector(getMarketDenomination)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const imageUrl = useAppSelector(getTruckImageState).imageUrl
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const waitingForResult = useRef(false)
  const truckInfo = useAppSelector(getTruckInfoState)
  const [pageReady, setPageReady] = useState<boolean>(false)
  const pageDescriptionHidden = useAppSelector(
    getGuidedViewPageDescriptionHidden,
  )
  const menuTree = useAppSelector(getMenuState).menuResultGv?.menuTree
  const menuLevel2 = menuTree?.subMenus
    ? menuTree?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel3 = menuLevel2
    ? menuLevel2?.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()

  useEffect(() => {
    if (imageUrl) {
      setPageReady(true)
    }
  }, [imageUrl])

  useEffect(() => {
    dispatch(setGuidedViewLoadTime(Date.now()))
  }, [dispatch])

  const handleOpenFullPageImageViewer = useCallback(() => {
    dispatch(setShowFullPageViewer(true))

    const trackingEvent: ScaniaAdobeTrackingClickFullImageEvent = {
      event: ScaniaAdobeEventId.FullImageClick,
    }
    pushAdobeEvent(trackingEvent)
  }, [dispatch])

  // Adobe user tracking.
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    dispatch(
      pushPageViewTrackingEvent({
        pageName: ScaniaAdobeTrackingPageName.GvGv,
        marketLanguage,
      }),
    )
  }, [dispatch, marketLanguage])

  const handleItemSelection = useCallback(
    async (selectedId: string, text: string) => {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!marketLanguage) {
        throw new Error('Expected marketLanguage to be defined.')
      }
      if (!client) {
        throw new Error('Expected client to be defined.')
      }
      if (waitingForResult.current) {
        console.log('Ignoring selection, waiting for previous result.')
        return
      }
      const isSmartDashCampaignItem = selectedId.startsWith(
        SMART_DASH_FAMILY_ID + '~',
      )
      const smartDashCampaignPreferredSeriesFrame: ImageSeriesFrame = {
        seriesId: 'INTERIOR',
        frameId: 1,
      }

      const trackingEvent: ScaniaAdobeTrackingConfigChangedEvent = {
        event: ScaniaAdobeEventId.ConfigChanged,
        eventInfo: {
          optionSelectedId: selectedId,
          optionSelectedText: text,
        },
      }
      pushAdobeEvent(trackingEvent)

      const sessionId = sessionInitData.sessionId
      waitingForResult.current = true
      let tryChangeConfigResponse
      try {
        dispatch(setCurrentlyLoadingId(selectedId))
        tryChangeConfigResponse = await client.tryChangeConfig({
          sessionId,
          input: selectedId,
        })
      } catch {
        dispatch(setCurrentlyLoadingId(null))
        handleFatalError()
        waitingForResult.current = false
        return
      }
      if (tryChangeConfigResponse.error === SESSION_FAILURE) {
        dispatch(setCurrentlyLoadingId(null))
        handleSessionFailure()
        return
      }
      if (!tryChangeConfigResponse.success) {
        dispatch(setCurrentlyLoadingId(null))
        handleFatalError()
        return
      }
      const selectionResult = tryChangeConfigResponse.success

      const handleConfigChangeResult = (configChange: ConfigChangeResult) => {
        const updates = configChange.updates
        if (!updates) {
          dispatch(setCurrentlyLoadingId(null))
          throw new Error('Expected updates to be defined.')
        }
        dispatch(setUserSelectionTimestamp(new Date().getTime()))
      }

      const resultCode = selectionResult.resultCode
      if (resultCode === 'ACTIVATED') {
        dispatch(setCurrentlyLoadingId(null))
        if (!selectionResult.changeConfigResult) {
          throw new Error('Expected .changeConfigResult to be defined.')
        }

        dispatch(
          setLatestConfigChangeResult(selectionResult.changeConfigResult),
        )
        handleConfigChangeResult(selectionResult.changeConfigResult)

        const newMarketDenomination =
          selectionResult.changeConfigResult.auxCarrier.auxiliaries[
            'MarketDenomination'
          ].value
        dispatch(setMarketDenomination(newMarketDenomination || null))
        const unavailable = getUnavailableFrames(
          selectionResult.changeConfigResult,
        )
        dispatch(setUnavailableImageFrames(unavailable))
        if (isSmartDashCampaignItem) {
          dispatch(setSeriesFrame(smartDashCampaignPreferredSeriesFrame))
        }
      } else if (resultCode === 'RESOLVERS') {
        // TODO: THIS IS IMPORTANT AND WILL SIMPLIFY THE SCDS_BACKEND CODE AND
        // THIS GUI CODE SIGNIFICANTLY. Change the Consequence Of Change
        // handling to require a explicit rollback/cancel request instead of the
        // current solution where the rollback is implicit at the next
        // configuration update interaction unless the Consequence Of Change
        // suggestion is confirmed by the `bip` request.
        //
        // Change to EXPLICIT ROLLBACK as a dedicated request sent by the
        // client!
        if (!selectionResult.resolvers) {
          dispatch(setCurrentlyLoadingId(null))
          throw new Error('Expected selectionResult.resolvers to be defined.')
        }

        var cocHasItemsIncludedInGv = false
        const addedItems = selectionResult.resolvers.triggerItems
        const removedItems = selectionResult.resolvers.deactivatedItems

        const clickedGuiGroupId = addedItems.find(
          (item) => item.id === selectedId,
        )?.guiGroup
        const clickedAddedId = addedItems.find(
          (item) => item.guiGroup === clickedGuiGroupId,
        )?.id
        const clickedRemovedId = removedItems.find(
          (item) => item.guiGroup === clickedGuiGroupId,
        )?.id

        selectionResult.resolvers.triggerItems.forEach((item) => {
          if (item.id === clickedAddedId) {
            return
          }
          if (item.includedInGuidedView) {
            cocHasItemsIncludedInGv = true
          }
        })
        selectionResult.resolvers.deactivatedItems.forEach((item) => {
          if (item.id === clickedRemovedId) {
            return
          }
          if (item.includedInGuidedView) {
            cocHasItemsIncludedInGv = true
          }
        })

        if (cocHasItemsIncludedInGv) {
          const cocData: ConsequenceOfChangeDialogData = {
            resolvers: selectionResult.resolvers,
            showCancelButton: true,
            clickedId: selectedId,
          }
          const choice = await handleConsequenceOfChange(
            cocData,
            CocOpenedFromType.guidedView,
          )
          switch (choice) {
            case 'CANCEL':
              dispatch(setCurrentlyLoadingId(null))
              dispatch(closeAllModals())
              handleCloseModalConsequenseOfChange()
              break
            case 'OK':
              const ids = selectionResult.resolvers.triggerItems.map(
                (item: ConfigChangeMenuEntry) => item.id,
              )
              const changeConfigResult = await client.changeConfig({
                sessionId,
                input: ids.join(';'),
              })
              dispatch(setCurrentlyLoadingId(null))
              if (changeConfigResult.error === SESSION_FAILURE) {
                handleSessionFailure()
                return
              }
              if (!changeConfigResult.success) {
                console.error('Expected .success to be defined.')
                handleFatalError()
                return
              }

              // TODO: Eliminate one of setLatestConfigChangeResult and
              // handleUserSelection!
              dispatch(setLatestConfigChangeResult(changeConfigResult.success))
              dispatch(
                setMarketDenomination(
                  changeConfigResult.success.auxCarrier.auxiliaries[
                    'MarketDenomination'
                  ].value || null,
                ),
              )
              const unavailable = getUnavailableFrames(
                changeConfigResult.success,
              )
              dispatch(setUnavailableImageFrames(unavailable))
              handleConfigChangeResult(changeConfigResult.success)

              dispatch(closeAllModals())
              handleCloseModalConsequenseOfChange()

              break
            default:
              console.error('Unknown choice variant: ' + choice)
              dispatch(setCurrentlyLoadingId(null))
              handleFatalError()
              return
          }
        } else {
          const ids = selectionResult.resolvers.triggerItems.map(
            (item: ConfigChangeMenuEntry) => item.id,
          )
          const changeConfigResult = await client.changeConfig({
            sessionId,
            input: ids.join(';'),
          })
          dispatch(setCurrentlyLoadingId(null))
          if (changeConfigResult.error === SESSION_FAILURE) {
            handleSessionFailure()
            return
          }
          if (!changeConfigResult.success) {
            console.error('Expected .success to be defined.')
            handleFatalError()
            return
          }

          dispatch(setLatestConfigChangeResult(changeConfigResult.success))
          dispatch(
            setMarketDenomination(
              changeConfigResult.success.auxCarrier.auxiliaries[
                'MarketDenomination'
              ].value || null,
            ),
          )
          const unavailable = getUnavailableFrames(changeConfigResult.success)
          dispatch(setUnavailableImageFrames(unavailable))
          handleConfigChangeResult(changeConfigResult.success)

          if (isSmartDashCampaignItem) {
            dispatch(setSeriesFrame(smartDashCampaignPreferredSeriesFrame))
          }
        }
      }
      waitingForResult.current = false

      const parent = menuLevel3?.find((menu) => menu.isOpen === true)
      const parentId = parent?.id
      if (!parentId) {
        throw new Error('Expected parent id for ' + selectedId)
      }
      const asyncWrapper = async () => {
        const newMenuState = await client.exploreMenu({
          sessionId: sessionInitData.sessionId,
          menuId: parentId,
          showAllConfigMenu: false,
        })
        if (newMenuState.success) {
          dispatch(setMenuTreeGv(newMenuState.success))
        }
      }
      asyncWrapper()
    },
    [
      client,
      dispatch,
      handleFatalError,
      handleSessionFailure,
      marketLanguage,
      sessionInitData,
      waitingForResult,
      menuLevel3,
      handleCloseModalConsequenseOfChange,
      handleConsequenceOfChange,
    ],
  )

  function getUnavailableFrames(
    rebopResult: ConfigChangeResult,
  ): ImageSeriesFrame[] {
    return (
      rebopResult.auxCarrier.auxiliaries['FrameFilter'].ignoredFrames
        ?.map<ImageSeriesFrame>((o) => ({
          seriesId: o.series,
          frameId: o.frame,
        }))
        .sort((a, b) => {
          const cmp = a.seriesId.localeCompare(b.seriesId)
          if (cmp !== 0) {
            return cmp
          }
          return a.frameId - b.frameId
        }) || []
    )
  }

  const handleHidePageDescription = () => {
    dispatch(setGuidedViewPageDescriptionHidden(!pageDescriptionHidden))
  }

  usePageUpdate(handleFatalError, handleSessionFailure)

  return (
    <GuidedViewRoot
      data-name="GuidedViewConfigurationRoot"
      id="scrollContainer"
      ref={rootRef}
      $pageIsReady={pageReady}
    >
      <SubHeader
        handleNewClick={handleNewClick}
        handleSessionFailure={handleSessionFailure}
        navigateToMenu={navigateToMenu}
      />
      <MainArea>
        <ImageSection ref={imageRef}>
          <HeaderAndChevronWrapper>
            <HeaderWrapper>
              <Header1>
                {t2(TextId.HEADER_GV_DESIGN_YOUR).toUpperCase()}
              </Header1>
              <Header2>
                {truckInfo?.savedAsName ||
                  truckInfo?.factoryModelName ||
                  marketDenomination}
              </Header2>
            </HeaderWrapper>
            <ChevronWrapper
              $hideText={pageDescriptionHidden}
              onClick={handleHidePageDescription}
              data-test-element-type={
                TestElementTypeId.GuidedViewDescriptionChevron
              }
            >
              <SvgChevronDown />
            </ChevronWrapper>
          </HeaderAndChevronWrapper>
          {!pageDescriptionHidden && (
            <DescriptionWrapper>
              <PageDescription>
                {t2(TextId.DESCRIPTION_GUIDED_VIEW)}
              </PageDescription>
              <GotItButtonWrapper>
                <TdsButton
                  size="sm"
                  variant="secondary"
                  onClick={handleHidePageDescription}
                  text={t2(TextId.LABEL_ACTION_GOT_IT)}
                  style={{ minWidth: 'fit-content' }}
                  data-test-element-type={
                    TestElementTypeId.GuidedViewGotItButton
                  }
                />
              </GotItButtonWrapper>
            </DescriptionWrapper>
          )}
          <ImageWrapper>
            <MainImageArea
              showImageControlPanel={true}
              showFullViewIcon={true}
              showSideArrows={true}
              navigateToMenu={navigateToMenu}
            />
            <FullViewWrapper
              onClick={handleOpenFullPageImageViewer}
              data-test-element-type={TestElementTypeId.GuidedViewMaximizeImage}
            >
              <SvgMaximize />
            </FullViewWrapper>
            <MainImageControlPanelMobile showIcons={true} />
          </ImageWrapper>
        </ImageSection>
        <MenuSection
          handleRfqOrEysClick={handleRfqOrEysClick}
          parentSize={size}
          handleItemSelection={handleItemSelection}
          highlightedId={highlightedId}
        />
        {!rfqOrEysButtonHidden && (
          <ButtonsWrapperMobile>
            <TdsButton
              variant="primary"
              text={rfqOrEysButtonText}
              size="md"
              onClick={handleRfqOrEysClick}
              fullbleed={
                size.width < BreakpointWidthPx.Small ||
                size.height < BreakpointHeightPx.Small
                  ? true
                  : false
              }
              style={
                size.width < BreakpointWidthPx.Small ||
                size.height < BreakpointHeightPx.Small
                  ? {}
                  : { width: 'fit-content' }
              }
              disabled={!rfqOrEysButtonEnabled}
              data-test-element-type={
                TestElementTypeId.GuidedViewGetConsultationButton
              }
            >
              <TdsIcon slot="icon" size="16px" name="message" />
            </TdsButton>
          </ButtonsWrapperMobile>
        )}
      </MainArea>

      <PortraitFooter
        displayAtScreenMaxWidth={BreakpointWidthPx.Small}
        displayAtScreenMaxHeight={BreakpointHeightPx.Small}
      />
    </GuidedViewRoot>
  )
}
